/**
 * Tech Câmbio Design System — derivação de cor de marca (whitelabel)
 * ---------------------------------------------------------------------------
 * O produto roda 5 tenants e o whitelabel é de COR e LOGOTIPO apenas. Só o
 * `primary` vem do theme.json do tenant; TODAS as cores derivadas dele (hover,
 * halo de foco, tingimento de linha, texto sobre a ação) são calculadas aqui.
 *
 * Por que calcular em vez de escrever à mão: os tenants ocupam extremos opostos
 * do espectro. O primary do symcapital é #000000 (e #FFFFFF no escuro) e o do
 * dolspay é #0A0A0A. Qualquer suposição fixa — "hover é sempre mais escuro",
 * "texto sobre a ação é sempre branco" — quebra nesses dois.
 *
 * DUAS MÉTRICAS DIFERENTES, DE PROPÓSITO:
 *
 *   · BRIGHTNESS (canal mais forte) decide clarear vs. escurecer no hover.
 *   · LUMINÂNCIA (WCAG) decide a cor do texto SOBRE a ação.
 *
 * Não troque uma pela outra. Usar luminância para o hover é um bug que já foi
 * pago duas vezes: o royal #1E22AA tem luminância 0,043 — na faixa de "quase
 * preto" — e cairia no ramo "clarear", invertendo o hover da marca. Pelo canal
 * mais forte (0xAA = 0,667) ele é corretamente tratado como cor média.
 */

const FALLBACK_PRIMARY = '#1E22AA';

/** Abaixo deste brightness, escurecer não produz diferença visível → clareia. */
const DARK_PIVOT = 0.25;

/** Contraste mínimo AA para texto sobre a cor de ação. */
const AA_CONTRAST = 4.5;

/**
 * @param {string} hex  #RGB ou #RRGGBB
 * @returns {{r:number,g:number,b:number}|null}
 */
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;

  let value = hex.trim().replace(/^#/, '');

  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }

  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;

  const int = parseInt(value, 16);

  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

export function rgbToHex({ r, g, b }) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));

  return `#${((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1)}`;
}

/**
 * Canal mais forte, normalizado. É o "quão escura a cor parece" para efeito de
 * manipulação — diferente de luminância, que pondera pela sensibilidade do olho
 * e afunda azuis saturados.
 */
export function brightness(rgb) {
  return Math.max(rgb.r, rgb.g, rgb.b) / 255;
}

/** Luminância relativa WCAG 2.1 — usada só para contraste de texto. */
export function relativeLuminance(rgb) {
  const channel = (c) => {
    const s = c / 255;

    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** Razão de contraste WCAG entre duas cores (1 a 21). */
export function contrastRatio(hexA, hexB) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);

  if (!a || !b) return 1;

  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);

  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Mistura a cor com preto (amount < 0) ou branco (amount > 0).
 * @param {string} hex
 * @param {number} amount  -1 a 1
 */
export function shade(hex, amount) {
  const rgb = hexToRgb(hex);

  if (!rgb) return hex;

  const target = amount > 0 ? 255 : 0;
  const ratio = Math.abs(amount);

  return rgbToHex({
    r: rgb.r + (target - rgb.r) * ratio,
    g: rgb.g + (target - rgb.g) * ratio,
    b: rgb.b + (target - rgb.b) * ratio
  });
}

/** rgba() a partir de um hex, para halos e tingimentos. */
export function alpha(hex, a) {
  const rgb = hexToRgb(hex);

  if (!rgb) return `rgba(30, 34, 170, ${a})`;

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}

/**
 * Texto/ícone sobre a cor de ação. Escolhe entre branco e o quase-preto da
 * marca pelo MAIOR contraste, e cai para preto puro se nenhum dos dois atingir
 * AA (acontece com cores de meio-tom como o dourado do dolspay).
 */
export function onColor(hex) {
  const white = contrastRatio(hex, '#FFFFFF');
  const ink = contrastRatio(hex, '#101820');

  if (white >= ink) {
    return white >= AA_CONTRAST ? '#FFFFFF' : '#101820';
  }

  return ink >= AA_CONTRAST ? '#101820' : '#000000';
}

/**
 * Contraste mínimo do shell com o texto branco que ele carrega.
 *
 * Bem acima de AA (4,5) porque o menu é superfície de permanência, mas não em 7:
 * os cinco primaries atuais vão de 6,9 (cambiohoje) a 21 (symcapital), e um piso
 * de 7 escureceria o cambiohoje por 0,1 — descaracterizando a marca para nada.
 * O piso existe para barrar cor inutilizável como fundo (o dourado do dolspay a
 * 1,7 ou o branco do symcapital a 1,0), não para afinar o que já funciona.
 */
const SHELL_MIN_CONTRAST = 6.5;

/**
 * Quanto da cor de marca fica no shell; o resto é o quase-preto institucional.
 *
 * 60% e não 100%: com a marca pura o menu ficava desigual demais entre tenants
 * — o cambiohoje (#4E44CD) e o apex (#0078D2) saíam vivos, a 6,9 e 6,8 de
 * contraste, enquanto symcapital e dolspay eram quase pretos a 21 e 19,8. A
 * mesma tela parecia de dois produtos diferentes. Com 60% a faixa fecha em
 * 8,1–19,9: escurece os vivos e quase não mexe nos que já eram escuros.
 */
const SHELL_BRAND_MIX = 0.6;

/**
 * Shell de navegação: a cor da marca aprofundada no quase-preto institucional.
 *
 * DOIS CUIDADOS, os dois já custaram bug:
 *
 * 1. Não use o `secondary`. Ele é `#0C2340` idêntico em todos os tenants — o
 *    menu ficava igual para todo mundo, ignorando a marca — e no tema escuro do
 *    bdayan é `#EE5F3F`, o que pintaria o menu inteiro de laranja.
 *
 * 2. Use o primary CANÔNICO (o do tema claro), nunca o do tema ativo. Os
 *    primaries do tema escuro são versões clareadas para funcionar como TEXTO
 *    sobre fundo escuro, e como FUNDO todos falham: o do dolspay é dourado
 *    (1,7 com branco), o do bernal é verde-claro e o do symcapital é `#FFFFFF`
 *    (1,0 — menu branco com texto branco). O shell é escuro nos dois temas,
 *    então não deve acompanhar a troca de tema.
 *
 * O laço final é a rede de segurança para um primary claro demais: o apex
 * (#0078D2) precisa dele mesmo depois da mistura.
 *
 * @param {string} primary  primary canônico (tema claro) do tenant
 * @returns {string} hex
 */
export function deriveShell(primary) {
  const marca = hexToRgb(primary) ? primary : FALLBACK_PRIMARY;
  const base = hexToRgb(marca);
  const ink = hexToRgb('#101820');

  let shell = rgbToHex({
    r: base.r * SHELL_BRAND_MIX + ink.r * (1 - SHELL_BRAND_MIX),
    g: base.g * SHELL_BRAND_MIX + ink.g * (1 - SHELL_BRAND_MIX),
    b: base.b * SHELL_BRAND_MIX + ink.b * (1 - SHELL_BRAND_MIX)
  });

  let passos = 0;

  while (contrastRatio(shell, '#FFFFFF') < SHELL_MIN_CONTRAST && passos < 24) {
    shell = shade(shell, -0.08);
    passos += 1;
  }

  return shell;
}

/**
 * Piso de saturação do acento. Sobre o shell escuro, uma marca pouco saturada
 * clareia para um cinza-azulado e deixa de ler como cor — o piso mantém o
 * acento cromático mesmo quando o primary é um azul-petróleo apagado.
 */
const ACCENT_SAT_MIN = 0.55;

/** Abaixo desta saturação a marca não tem matiz para esticar. */
const ACHROMATIC_SAT = 0.08;

/**
 * Contraste do acento contra o shell.
 *
 * 5,5 e não 4,5: o acento marca o item ATIVO do menu — é o que responde "onde
 * eu estou" de relance, não um texto que se lê parando em cima. O piso de AA
 * deixava o apex em 3,74 e o cambiohoje em 4,93, ambos apagados sobre o fundo.
 */
const ACCENT_MIN_CONTRAST = 5.5;

/** @returns {{h:number, s:number, l:number}} h em graus, s e l de 0 a 1. */
export function hexToHsl(hex) {
  const rgb = hexToRgb(hex) || hexToRgb(FALLBACK_PRIMARY);
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;

  if (d === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;

  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;

  h *= 60;

  return { h: h < 0 ? h + 360 : h, s, l };
}

/** @param {{h:number, s:number, l:number}} hsl */
export function hslToHex({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let p;

  if (h < 60) p = [c, x, 0];
  else if (h < 120) p = [x, c, 0];
  else if (h < 180) p = [0, c, x];
  else if (h < 240) p = [0, x, c];
  else if (h < 300) p = [x, 0, c];
  else p = [c, 0, x];

  return rgbToHex({ r: (p[0] + m) * 255, g: (p[1] + m) * 255, b: (p[2] + m) * 255 });
}

/**
 * Acento do shell: a marca esticada até destacar sobre o fundo do menu.
 *
 * É o par do `deriveShell` — os dois saem do mesmo primary, então a barra do
 * item ativo e o fundo em que ela vive pertencem à mesma família de cor.
 *
 * ANTES ISTO NÃO ERA DERIVADO: `--tc-shell-accent` lia o `accent` do Vuetify,
 * que os OITO tenants declaravam como `#82B1FF` — o azul do tema padrão do
 * Vuetify, nunca revisto. Sobre o roxo do cambiohoje ficava a 27° de matiz da
 * marca (lia como uma segunda cor), e sobre o azul do apex sobrava contraste
 * de 3,74. Era a única cor do shell que não vinha da marca.
 *
 * @param {string} primary  primary canônico (tema claro) do tenant
 * @param {string} [primaryDark]  primary do tema escuro, usado só quando a
 *   marca clara é acromática — é a cor que a marca já escolheu para viver sobre
 *   fundo escuro (no dolspay, o dourado).
 * @returns {string} hex
 */
export function deriveShellAccent(primary, primaryDark) {
  const marca = hexToRgb(primary) ? primary : FALLBACK_PRIMARY;
  let hsl = hexToHsl(marca);

  // Marca sem matiz (symcapital #000000, dolspay #0A0A0A): não há cor para
  // esticar — clarear preto só dá cinza.
  if (hsl.s < ACHROMATIC_SAT) {
    const alternativa = hexToRgb(primaryDark) ? hexToHsl(primaryDark) : { s: 0 };

    // Sem alternativa cromática o acento é o branco. É o caso do symcapital,
    // cuja identidade é preto e branco: o item ativo se distingue pelo fundo
    // tingido, não por cor.
    if (alternativa.s < ACHROMATIC_SAT) return '#FFFFFF';

    hsl = alternativa;
  }

  const shell = deriveShell(marca);
  const cor = { h: hsl.h, s: Math.max(hsl.s, ACCENT_SAT_MIN), l: hsl.l };
  let acento = hslToHex(cor);
  let passos = 0;

  while (contrastRatio(acento, shell) < ACCENT_MIN_CONTRAST && passos < 60) {
    cor.l = Math.min(0.95, cor.l + 0.02);
    acento = hslToHex(cor);
    passos += 1;
  }

  return acento;
}

/**
 * Deriva o conjunto completo de cores de marca a partir do primary do tenant.
 *
 * @param {string} primary  primary do tema ATIVO — define a cor de ação
 * @param {string} [shellPrimary]  primary CANÔNICO (tema claro) — define o
 *   shell de navegação, que é escuro nos dois temas e por isso não acompanha a
 *   troca de tema. Omitir só é seguro quando os dois coincidem.
 * @param {object} [opcoes]
 * @param {string} [opcoes.darkPrimary]  primary do tema escuro, só para o acento
 *   de marca acromática — ver deriveShellAccent.
 * @param {string} [opcoes.accent]  acento DECLARADO pelo tenant no theme.json.
 *   Quando existe, manda: a derivação é o padrão, não a lei.
 * @returns {{action:string, actionHover:string, actionOn:string,
 *            actionHalo:string, actionTint:string, actionTintStrong:string,
 *            shell:string, shellAccent:string}}
 */
export function deriveBrand(primary, shellPrimary, opcoes = {}) {
  const { darkPrimary, accent } = opcoes;
  const action = hexToRgb(primary) ? primary : FALLBACK_PRIMARY;
  const canonico = hexToRgb(shellPrimary) ? shellPrimary : action;
  const rgb = hexToRgb(action);
  const b = brightness(rgb);

  // Cor muito escura não tem para onde escurecer — o hover tem que clarear.
  // Cor muito clara (branco do symcapital no escuro) tem que escurecer.
  let hover;

  if (b < DARK_PIVOT) {
    hover = shade(action, 0.22);
  } else if (b > 0.92) {
    hover = shade(action, -0.14);
  } else {
    hover = shade(action, -0.16);
  }

  return {
    action,
    actionHover: hover,
    actionOn: onColor(action),
    actionHalo: alpha(action, 0.1),
    actionTint: alpha(action, 0.04),
    actionTintStrong: alpha(action, 0.1),
    shell: deriveShell(canonico),
    // O acento declarado vence o derivado — é assim que um tenant escolhe uma
    // cor própria. A derivação é a rede: tenant sem acento (ou com valor
    // inválido) nunca fica sem, nem herda o azul de um tema padrão.
    shellAccent: hexToRgb(accent) ? accent : deriveShellAccent(canonico, darkPrimary)
  };
}

/**
 * Injeta os tokens de marca como custom properties no elemento raiz.
 * Chamado no bootstrap e de novo a cada troca de tema/tenant.
 *
 * @param {string} primary  primary do tema ativo
 * @param {string} [shellPrimary]  primary canônico (tema claro), para o shell
 * @param {object} [opcoes]
 * @param {string} [opcoes.darkPrimary]  primary do tema escuro, para o acento de
 *   marca acromática
 * @param {string} [opcoes.accent]  acento declarado pelo tenant
 * @param {HTMLElement} [opcoes.root]
 */
export function applyBrandTokens(primary, shellPrimary, opcoes = {}) {
  const target =
    opcoes.root || (typeof document !== 'undefined' && document.documentElement);

  if (!target) return null;

  const brand = deriveBrand(primary, shellPrimary, opcoes);

  target.style.setProperty('--tc-action', brand.action);
  target.style.setProperty('--tc-action-hover', brand.actionHover);
  target.style.setProperty('--tc-action-on', brand.actionOn);
  target.style.setProperty('--tc-action-halo', brand.actionHalo);
  target.style.setProperty('--tc-action-tint', brand.actionTint);
  target.style.setProperty('--tc-action-tint-strong', brand.actionTintStrong);
  target.style.setProperty('--tc-shell', brand.shell);
  target.style.setProperty('--tc-shell-accent', brand.shellAccent);

  return brand;
}

export default {
  deriveBrand,
  deriveShellAccent,
  applyBrandTokens,
  contrastRatio,
  brightness,
  relativeLuminance
};
